import { useState, useEffect, useRef, useCallback } from "react";
import { Chess } from "chess.js";
import { stockfishSocketService } from "./StockfishSocketService";

export function useStockfishPersonalised({
  onNewPuzzle,
  onPuzzleGenerationComplete,
}) {
  const [isSocketConnected, setIsSocketConnected] = useState(
    stockfishSocketService.isConnected
  );
  const [analysisData, setAnalysisData] = useState({
    bestMove: null,
    evaluation: null,
    pv: "",
    depth: 0,
    allLines: [],
    currentFen: null,
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingPuzzles, setIsGeneratingPuzzles] = useState(
    stockfishSocketService.isGeneratingPuzzles
  );
  const [analysisRun, setAnalysisRun] = useState(
    stockfishSocketService.analysisRunForSession
  );
  const oneShotAnalysisResolverRef = useRef(null);

  useEffect(() => {
    const handleSocketMessage = (serverMessage) => {
      if (serverMessage.type === "new_puzzle_found") {
        onNewPuzzle?.(serverMessage.payload);
      } else if (serverMessage.type === "puzzle_generation_complete") {
        onPuzzleGenerationComplete?.();
      } else if (serverMessage.type === "info") {
        setIsAnalyzing(true);
        const firstLine = serverMessage.data?.[0] || {};
        let currentEval = null;
        const fenBeingAnalyzed = serverMessage.fen;
        if (fenBeingAnalyzed) {
          const turn = new Chess(fenBeingAnalyzed).turn();
          const multiplier = turn === "b" ? -1 : 1;
          if (firstLine.rawMate != null)
            currentEval = {
              type: "mate",
              value: firstLine.rawMate * multiplier,
            };
          else if (firstLine.rawCp != null)
            currentEval = { type: "cp", value: firstLine.rawCp * multiplier };
        }

        setAnalysisData({
          bestMove: firstLine.pvUci?.split(" ")[0] || null,
          evaluation: currentEval,
          pv: firstLine.pvUci || "",
          depth: serverMessage.depth || firstLine.depth || 0,
          allLines: serverMessage.data || [],
          currentFen: serverMessage.fen,
        });
      } else if (serverMessage.type === "bestmove") {
        setIsAnalyzing(false);
        if (oneShotAnalysisResolverRef.current) {
          oneShotAnalysisResolverRef.current.resolve(
            serverMessage.allLines || []
          );
          oneShotAnalysisResolverRef.current = null;
        }
      } else if (serverMessage.type === "error") {
        console.warn("[Stockfish Hook] Backend error:", serverMessage.message);
        setIsAnalyzing(false);

        setAnalysisData({
          bestMove: null,
          evaluation: null,
          pv: "",
          depth: 0,
          allLines: [],
          currentFen: null,
        });

        if (oneShotAnalysisResolverRef.current) {
          oneShotAnalysisResolverRef.current.reject(
            new Error(serverMessage.message || "Backend error")
          );
          oneShotAnalysisResolverRef.current = null;
        }
      }
    };

    const subscriber = {
      onConnect: () => setIsSocketConnected(true),
      onDisconnect: () => setIsSocketConnected(false),
      onMessage: handleSocketMessage,
      onGenerationStatusChange: (isGenerating) => {
        setIsGeneratingPuzzles(isGenerating);
      },
    };

    stockfishSocketService.subscribe(subscriber);

    return () => {
      stockfishSocketService.unsubscribe(subscriber);
    };
  }, [onNewPuzzle, onPuzzleGenerationComplete]);

  const startAnalysis = useCallback(
    (fen, analysisMode = "infinite", targetDepth = null, multiPv = 1) => {
      setAnalysisData({
        bestMove: null,
        evaluation: null,
        pv: "",
        depth: 0,
        allLines: [],
        currentFen: fen,
      });
      setIsAnalyzing(true);
      stockfishSocketService.send("start_analysis", {
        fen,
        mode: analysisMode,
        targetDepth,
        multiPv,
      });
    },
    []
  );

  const stopAnalysis = useCallback(() => {
    stockfishSocketService.send("stop_analysis", {});
    setIsAnalyzing(false);
  }, []);

  const sendPuzzleGenerationCommand = useCallback((games, userData) => {
    console.log(
      `[useStockfishPersonalised] Sending puzzle generation command via service.`
    );
    const gamesPayload = games.map((game) => ({
      pgn: game.pgn,
      url: game.url,
      source: game.source,
      white: game.white,
      black: game.black,
    }));
    stockfishSocketService.send("start_puzzle_generation", {
      games: gamesPayload,
      userData,
    });
  }, []);

  const getOneShotEvaluation = useCallback((fen, options = { depth: 20 }) => {
    return new Promise((resolve, reject) => {
      if (!stockfishSocketService.isConnected) {
        return reject(
          new Error("Cannot get evaluation: WebSocket not connected.")
        );
      }
      const timeoutId = setTimeout(() => {
        oneShotAnalysisResolverRef.current = null;
        reject(new Error("One-shot evaluation timed out."));
      }, 15000);

      oneShotAnalysisResolverRef.current = {
        resolve: (result) => {
          clearTimeout(timeoutId);
          resolve(result);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        },
      };

      stockfishSocketService.send("start_analysis", {
        fen,
        mode: `depth ${options.depth}`,
        multiPv: 1,
      });
    });
  }, []);

  return {
    isSocketConnected,
    isAnalyzing,
    isGeneratingPuzzles,
    analysisData,
    analysisRun,
    startAnalysis,
    stopAnalysis,
    sendPuzzleGenerationCommand,
    getOneShotEvaluation,
  };
}
